// Effects of light: sunbeams, a glow that breathes, and glints on water. Each
// finds its light in the picture (the brightest place high up, the warmest
// light, the bright ripples low down) unless the theme says where it is, and
// adds to what is there with screen, so it never covers the picture.

import { css, layer, rng, type RGB } from "../scenes";
import { calm, clamp01, dither, lift, mixRGB, smooth, softDot, speckle, type Effect } from "./kit";
import { hottest, rgbAt, specks } from "./map";

// ---------------------------------------------------------------- sunbeams

/** One shaft of light: narrow where it starts, wider as it goes, soft at its sides, gone by its end. */
function shaft(rgb: RGB): HTMLCanvasElement {
  const W = 64;
  const H = 256;
  const [c, ctx] = layer(W, H);
  const img = ctx.createImageData(W, H);
  for (let v = 0; v < H; v++) {
    const f = v / (H - 1);
    const half = 0.18 + 0.82 * f;
    const along = smooth(0, 0.22, f) * (1 - f) ** 1.5;
    for (let i = 0; i < W; i++) {
      const d = Math.abs(i + 0.5 - W / 2) / ((W / 2) * half);
      const a = Math.exp(-d * d * 1.6) * along;
      const p = (v * W + i) * 4;
      img.data[p] = rgb[0];
      img.data[p + 1] = rgb[1];
      img.data[p + 2] = rgb[2];
      img.data[p + 3] = Math.round(clamp01(a) * 255);
    }
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

interface Beam {
  ang: number;
  wide: number;
  s: number;
  p: number;
}

interface Speck {
  x: number;
  y: number;
  p: number;
  z: number;
}

export const sunbeams: Effect = {
  fps: 15,
  res: 0.5,
  blend: "screen",
  build(env) {
    const r = rng(env.seed * 53 + 1);
    const { w, h } = env;
    // from the theme's light, or the brightest place high in the picture, or high on the right
    const found = env.map ? hottest(env.map, [0, 0, 1, 0.45], 0.3) : null;
    const sx = env.x ?? found?.x ?? 0.8;
    const sy = Math.min(env.y ?? found?.y ?? 0, 0.18) - 0.1;
    // sunlight is warm: the light's own color, leaning gold
    const col = env.color ?? mixRGB(found ? lift(found.rgb, 0.3) : [255, 236, 204], [255, 214, 160], 0.45);
    const tex = shaft(col);
    // they fan down and away from the middle of the screen, where the tiles are, toward the far lower corner
    const toward = Math.atan2(h - sy * h, (sx < 0.5 ? w * 0.92 : w * 0.08) - sx * w);
    const m = 5 + Math.round(2 * env.strength);
    const beams: Beam[] = Array.from({ length: m }, (_, i) => ({ ang: toward + (i - (m - 1) / 2) * (0.7 / m) + (r() - 0.5) * 0.1, wide: (0.09 + r() * 0.12) * w, s: 0.08 + r() * 0.16, p: r() * 6.283 }));
    const n = Math.round(18 + 30 * env.strength);
    const dust: Speck[] = Array.from({ length: n }, () => {
      const b = beams[Math.floor(r() * m)];
      const d = (0.15 + r() * 0.7) * Math.hypot(w, h) * 0.8;
      return { x: sx * w + Math.cos(b.ang) * d + (r() - 0.5) * b.wide * 0.6, y: sy * h + Math.sin(b.ang) * d, p: r() * 100, z: r() };
    });
    return { tex, beams, dust, sx: sx * w, sy: sy * h, len: Math.hypot(w, h) * 1.15, mote: softDot(lift(col, 0.3), 3 * env.u, 0.3) };
  },
  frame(ctx, t, state, env) {
    const s = state as { tex: HTMLCanvasElement; beams: Beam[]; dust: Speck[]; sx: number; sy: number; len: number; mote: HTMLCanvasElement };
    const { w, h, u } = env;
    ctx.clearRect(0, 0, w, h);
    ctx.globalCompositeOperation = "lighter";
    const k = 0.05 + env.strength * 0.08;
    const lit: number[] = [];
    for (const b of s.beams) {
      const a = k * (0.45 + 0.55 * (0.5 + 0.5 * Math.sin(t * b.s + b.p)));
      lit.push(a / k);
      ctx.save();
      ctx.translate(s.sx, s.sy);
      ctx.rotate(b.ang - Math.PI / 2);
      ctx.globalAlpha = a;
      ctx.drawImage(s.tex, -b.wide / 2, 0, b.wide, s.len);
      ctx.restore();
    }
    // dust turning in the light: brighter inside a beam, almost gone outside one
    for (const d of s.dust) {
      const x = d.x + Math.sin(t * 0.13 + d.p) * 22 * u + Math.sin(t * 0.05 + d.p * 2) * 12 * u;
      const y = d.y + Math.cos(t * 0.11 + d.p) * 16 * u - t * 1.5 * u * (0.5 + d.z);
      const ang = Math.atan2(y - s.sy, x - s.sx);
      const dist = Math.hypot(x - s.sx, y - s.sy);
      let inBeam = 0;
      s.beams.forEach((b, i) => {
        const off = Math.abs(Math.sin(ang - b.ang)) * dist;
        const half = (b.wide / 2) * (0.12 + 0.88 * Math.min(1, dist / s.len));
        inBeam = Math.max(inBeam, Math.exp(-((off / half) ** 2) * 2) * lit[i]);
      });
      const tw = 0.5 + 0.5 * Math.sin(t * (0.6 + d.z) + d.p);
      ctx.globalAlpha = clamp01((0.06 + 0.8 * inBeam) * (0.4 + 0.6 * tw));
      const size = s.mote.width * (0.6 + 0.8 * d.z);
      ctx.drawImage(s.mote, x - size / 2, y - size / 2, size, size);
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
  },
};

// ---------------------------------------------------------------- glow

export const glow: Effect = {
  fps: 12,
  res: 0.35,
  blend: "screen",
  build(env) {
    // the warmest light, but not one hidden behind the tiles in the middle when another will do
    const found = env.x === undefined && env.y === undefined && env.map ? hottest(env.map, [0, 0, 1, 1], 0.6, true) : null;
    const x = env.x ?? found?.x ?? 0.5;
    const y = env.y ?? found?.y ?? 0.3;
    const col = env.color ?? (env.map ? lift(rgbAt(env.map, x, y), 0.25) : ([255, 186, 120] as RGB));
    const [c, ctx] = layer(256, 256);
    const g = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    // a falloff close to a bell curve, so the glow has no edge anywhere
    for (let i = 0; i <= 12; i++) {
      const f = i / 12;
      g.addColorStop(f, css(i === 0 ? lift(col, 0.3) : col, Math.exp(-f * f * 5.5) * (1 - f)));
    }
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 256, 256);
    return { tex: c, x, y, r: (0.17 + 0.12 * env.strength) * Math.max(env.w, env.h), grain: speckle(96, 0.22) };
  },
  frame(ctx, t, state, env) {
    const s = state as { tex: HTMLCanvasElement; x: number; y: number; r: number; grain: HTMLCanvasElement };
    ctx.clearRect(0, 0, env.w, env.h);
    // one slow breath every nine seconds or so, never quite the same twice
    const breath = 0.5 + 0.5 * Math.sin((t * 2 * Math.PI) / 9 + 0.6 * Math.sin(t * 0.13));
    ctx.globalAlpha = (0.14 + 0.2 * env.strength) * (0.62 + 0.38 * breath);
    const r = s.r * (0.94 + 0.06 * breath);
    ctx.drawImage(s.tex, s.x * env.w - r, s.y * env.h - r, r * 2, r * 2);
    dither(ctx, s.grain, env.w, env.h);
  },
};

// ---------------------------------------------------------------- glints

interface Glint {
  x: number;
  y: number;
  b: number;
  t0: number;
  life: number;
  star: boolean;
}

export const glints: Effect = {
  fps: 24,
  res: 1,
  blend: "screen",
  build(env) {
    const r = rng(env.seed * 59 + 7);
    // the bright ripples low in the picture, or a band of water across its foot
    let spots = env.map ? specks(env.map, [0, 0.42, 1, 1], { max: 600, min: 0.28, lift: 0.035 }) : [];
    if (spots.length < 24) spots = Array.from({ length: 200 }, () => ({ x: 0.04 + r() * 0.92, y: 0.62 + r() * 0.33, b: 0.12 }));
    const weights: number[] = [];
    let total = 0;
    for (const s of spots) weights.push((total += 0.02 + s.b));
    const pick = () => {
      const v = r() * total;
      let lo = 0;
      let hi = weights.length - 1;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (weights[mid] < v) lo = mid + 1;
        else hi = mid;
      }
      return spots[lo];
    };
    const col = env.color ?? (env.map ? lift(mixRGB(rgbAt(env.map, spots[0].x, spots[0].y), [255, 244, 224], 0.6), 0.2) : ([255, 246, 228] as RGB));
    const n = Math.round(18 + 54 * env.strength);
    const born = (t: number): Glint => {
      const s = pick();
      return { x: s.x + (r() - 0.5) * 0.006, y: s.y + (r() - 0.5) * 0.004, b: clamp01(0.45 + s.b * 3), t0: t, life: 0.5 + r() * 0.9, star: r() < 0.12 };
    };
    const live: Glint[] = Array.from({ length: n }, () => {
      const g = born(0);
      g.t0 = -r() * g.life;
      return g;
    });
    // a glint is wider than it is tall, as light lies on water
    const [flat, fctx] = layer(Math.ceil(30 * env.u) + 2, Math.ceil(8 * env.u) + 2);
    fctx.scale(flat.width / flat.height, 1);
    fctx.drawImage(softDot(col, flat.height / 2, 0.2), 0, 0);
    return { live, born, flat, sparkle: softDot(lift(col, 0.4), 4 * env.u, 0.3), r };
  },
  frame(ctx, t, state, env) {
    const s = state as { live: Glint[]; born: (t: number) => Glint; flat: HTMLCanvasElement; sparkle: HTMLCanvasElement; r: () => number };
    const { w, h } = env;
    ctx.clearRect(0, 0, w, h);
    const k = 0.5 + env.strength * 0.6;
    for (let i = 0; i < s.live.length; i++) {
      let g = s.live[i];
      if (t - g.t0 > g.life) {
        g = s.born(t + s.r() * 0.35);
        s.live[i] = g;
      }
      const age = t - g.t0;
      if (age < 0) continue;
      const a = Math.sin((Math.PI * age) / g.life) ** 2 * g.b * k * (0.6 + 0.4 * calm(g.x, g.y));
      if (a < 0.02) continue;
      ctx.globalAlpha = Math.min(1, a);
      ctx.drawImage(s.flat, g.x * w - s.flat.width / 2, g.y * h - s.flat.height / 2);
      if (g.star) ctx.drawImage(s.sparkle, g.x * w - s.sparkle.width / 2, g.y * h - s.sparkle.height / 2);
    }
    ctx.globalAlpha = 1;
  },
};
